// Code generated by master-scheme ServiceTypesGenerator - DO NOT EDIT
// Generator: /Users/victorvins/Projects/master-scheme/dsl/src/ts-api/ServiceTypesGenerator.scala

import { postEndpoint } from '@innolab-trader/tradovate-transport'
import type { ValidationError } from '@innolab-trader/tradovate-types'
import type { MFAOptions } from '@innolab-trader/tradovate-types'
import type { HibpHint } from '@innolab-trader/tradovate-types/src/enums/hibpHint'
import type { UserExperience } from '@innolab-trader/tradovate-types/src/enums/userExperience'
import type { UserStatus } from '@innolab-trader/tradovate-types/src/enums/userStatus'

export interface AccessTokenRequest {
  hibpCheck?: boolean
  name: string
  password: string
  coapplicantCode?: string
  appId?: string
  appVersion?: string
  deviceId?: string
  userAgent?: string
  organization?: string
  cid?: string
  sec?: string
  chl?: string
  locale?: string
  enc?: boolean
}

export interface AccessTokenResponse {
  errorText?: string
  hibpHint?: HibpHint
  accessToken?: string
  mdAccessToken?: string
  expirationTime?: string
  passwordExpirationTime?: string
  userStatus?: UserStatus
  userId?: number
  name?: string
  hasLive?: boolean
  hasSimPlus?: boolean
  outdatedTaC?: boolean
  hasFunded?: boolean
  hasMarketData?: boolean
  requiredNonProCertification?: boolean
  newTrialEligible?: boolean
  outdatedLiquidationPolicy?: boolean
  outdatedSentimentPolicy?: boolean
  mfa?: MFAOptions
  experience?: UserExperience
  orgName?: string
}

/**
 * Validate AccessTokenRequest request
 *
 * Follows Rust validation pattern with structured errors
 *
 * @param request - The request object to validate
 * @returns Array of validation errors, empty if valid
 *
 * @example
 * ```typescript
 * const errors = validateAccessTokenRequest(requestData)
 * if (errors.length > 0) {
 *   return { status: 400, errors }
 * }
 * ```
 */
export function validateAccessTokenRequest(request: AccessTokenRequest): ValidationError[] {
  const errors: ValidationError[] = []

  const req = request as unknown as Record<string, unknown>
  if (req.name === undefined || req.name === null) {
    errors.push({
      field: 'name',
      code: 'required',
      message: 'name is required'
    })
  }
  if (typeof req.name === 'string' && req.name.length > 64) {
    errors.push({
      field: 'name',
      code: 'too_long',
      message: 'name must not exceed 64 characters',
      argument: '64'
    })
  }
  if (typeof req.name === 'string' && req.name.trim() === '') {
    errors.push({
      field: 'name',
      code: 'required',
      message: 'name cannot be empty'
    })
  }

  if (req.password === undefined || req.password === null) {
    errors.push({
      field: 'password',
      code: 'required',
      message: 'password is required'
    })
  }
  if (typeof req.password === 'string' && req.password.length > 512) {
    errors.push({
      field: 'password',
      code: 'too_long',
      message: 'password must not exceed 512 characters',
      argument: '512'
    })
  }
  if (typeof req.password === 'string' && req.password.trim() === '') {
    errors.push({
      field: 'password',
      code: 'required',
      message: 'password cannot be empty'
    })
  }

  if (typeof req.coapplicantCode === 'string' && req.coapplicantCode.length > 8192) {
    errors.push({
      field: 'coapplicantCode',
      code: 'too_long',
      message: 'coapplicantCode must not exceed 8192 characters',
      argument: '8192'
    })
  }

  if (typeof req.appId === 'string' && req.appId.length > 64) {
    errors.push({
      field: 'appId',
      code: 'too_long',
      message: 'appId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.appVersion === 'string' && req.appVersion.length > 64) {
    errors.push({
      field: 'appVersion',
      code: 'too_long',
      message: 'appVersion must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.deviceId === 'string' && req.deviceId.length > 64) {
    errors.push({
      field: 'deviceId',
      code: 'too_long',
      message: 'deviceId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.userAgent === 'string' && req.userAgent.length > 512) {
    errors.push({
      field: 'userAgent',
      code: 'too_long',
      message: 'userAgent must not exceed 512 characters',
      argument: '512'
    })
  }

  if (typeof req.organization === 'string' && req.organization.length > 64) {
    errors.push({
      field: 'organization',
      code: 'too_long',
      message: 'organization must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.cid === 'string' && req.cid.length > 64) {
    errors.push({
      field: 'cid',
      code: 'too_long',
      message: 'cid must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.sec === 'string' && req.sec.length > 8192) {
    errors.push({
      field: 'sec',
      code: 'too_long',
      message: 'sec must not exceed 8192 characters',
      argument: '8192'
    })
  }

  if (typeof req.chl === 'string' && req.chl.length > 64) {
    errors.push({
      field: 'chl',
      code: 'too_long',
      message: 'chl must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.locale === 'string' && req.locale.length > 64) {
    errors.push({
      field: 'locale',
      code: 'too_long',
      message: 'locale must not exceed 64 characters',
      argument: '64'
    })
  }

  return errors
}

export const accessTokenRequest = postEndpoint<AccessTokenRequest, AccessTokenResponse>('/auth/accessTokenRequest')
